/**
 * Types and pure helpers for the review workspace.
 *
 * The domain object is a `Review` of a printed label against its approved
 * master. Each `Finding` carries four independent axes; the axes must be
 * modelled and rendered independently. Collapsing them into a single
 * status chip loses meaningful distinctions — confirmed-and-corrected,
 * confirmed-and-open, and dismissed-and-disputed are all legitimate
 * combined states, and the client report is gated on the intersection.
 *
 * This module owns the UI types and format-agnostic helpers. The API layer
 * (`api/`) builds on top of it via the wire→UI adapter; feature components
 * import types from here but never mutate them.
 */

export type ReviewId = string & { __brand: "ReviewId" }
export type FindingId = string & { __brand: "FindingId" }
export type MessageId = string & { __brand: "MessageId" }
export type UserId = string & { __brand: "UserId" }

/** Severity is the rule's own reading — how bad it is if the divergence is real. */
export type FindingSeverity = "error" | "warning" | "info"

/**
 * The four independent axes that describe a finding's lifecycle. Each
 * axis moves on its own; consumers must render them independently. The
 * spine primitive shows all four as parallel bars in the record's gutter.
 */
export interface FindingAxes {
  /** Is it real? Reviewer/specialist call. */
  verdict: "unset" | "confirmed" | "rejected" | "needs_sme"
  /** Is it fixed? Pipeline proposes candidate_corrected; a human confirms. */
  state: "open" | "candidate_corrected" | "corrected_confirmed" | "resolved"
  /** Did the client agree? Only meaningful once the client sees the report. */
  clientResolution: "none" | "acknowledged" | "disputed" | "done"
  /** Where did it come from? Set at creation; never changes. */
  origin: "new" | "carried_over" | "regression" | "manual"
}

/** Where the finding sits on the artefact. Drives whether an overlay box is drawn. */
export type FindingPlacement = "box" | "page" | "document"

/**
 * A finding's location expressed in PDF point space. Storing points (not
 * pixels) is what lets zoom, DPR, and container resize compose without
 * per-consumer math; the surface converts to CSS pixels at draw time.
 */
export interface PointRect {
  page: number
  x0: number
  y0: number
  x1: number
  y1: number
}

/** Two-sided evidence: what the master says vs. what the printed label shows. */
export interface Evidence {
  master: string | null
  printed: string | null
}

/** A regulatory citation. `kind` selects the resolver (eCFR, LRM chapter, etc.). */
export interface Authority {
  kind: "statute" | "cfr" | "lrm" | "prn" | "internal"
  label: string
  href?: string
}

/**
 * Backend model: Comment. `authorSide` is derived from the principal
 * server-side and cannot be forged from the wire. Visibility is not a
 * per-message toggle — every comment on a review is equally visible to
 * every side that has access.
 */
export interface FindingComment {
  id: MessageId
  authorName: string
  authorSide: AuthorSide
  body: string
  at: string
}

/** Backend enum. `ai` is the pipeline-authored side, not a human role. */
export const AuthorSide = {
  REVIEWER: "reviewer",
  CLIENT: "client",
  AI: "ai",
} as const
export type AuthorSide = (typeof AuthorSide)[keyof typeof AuthorSide]

/** KPI grouping — which "check group" this finding rolls up to on the report. */
export interface FindingGroup {
  id: string
  label: string
  ordinal: number
}

export interface Finding {
  id: FindingId
  /** Display number — position in the label. Recomputed on the server. */
  number: number
  ruleId: string
  severity: FindingSeverity
  claim: string
  placement: FindingPlacement
  /** Only present when placement = "box"; overlay reads this. */
  bbox: PointRect | null
  /** For placement = "page"; the page the callout ties to. */
  pageHint: number | null
  occurrences: number
  axes: FindingAxes
  /** Pipeline's own confidence, 0–1. Absent on manually-authored findings. */
  confidence: number | null
  authoredBy: { name: string; at: string } | null
  group: FindingGroup
  kind: "mechanical" | "judgment" | "hybrid"
  divergence: Evidence | null
  reasoning: string | null
  remediation: string | null
  authorities: Authority[]
  /** Version the finding was first seen in. Drives the "carried over from v2" chip. */
  firstSeenVersion: string
  commentCount: number
  unreadCount: number
  smeNote: { by: string; at: string; note: string } | null
  dismissalNote: { by: string; at: string; note: string | null } | null
}

/**
 * Backend workflow stage. Three stages only — this mirrors
 * ReviewWorkflowStage in the API. Archived is orthogonal (a boolean on
 * the review, not a stage) because an archived review preserves its last
 * stage and can be un-archived back into the queue.
 */
export type ReviewStage = "initial" | "secondary" | "done"

/**
 * The set of actions the assignment/routing endpoints can record. Every
 * hand-off is one of these; the trail below stores them verbatim so the
 * UI can render "reassigned from X to Y" as more than a stage change.
 */
export type AssignmentAction =
  | "assign"
  | "reassign"
  | "throwback"
  | "advance_stage"
  | "skip_secondary"
  | "archive"
  | "unarchive"

/**
 * One entry in the append-only assignment trail. Mirrors the backend
 * AssignmentEvent shape. `toStage` is nullable because throwback and
 * (un)archive don't move the stage; `fromUser`/`toUser` may both be
 * null on stage advances that don't touch assignment.
 */
export interface AssignmentEvent {
  action: AssignmentAction
  at: string
  actor: string | null
  fromUser: string | null
  toUser: string | null
  toStage: ReviewStage | null
  note: string | null
}

export interface ReviewAssignment {
  currentStage: ReviewStage
  /** Server-side flag. Archived reviews keep their last stage. */
  archived: boolean
  /** True once secondary was explicitly skipped; drives the trail badge. */
  secondarySkipped: boolean
  /** Null = in the pool, waiting for someone to pick up. */
  assignee: { userId: UserId; name: string; role: Role; sinceISO: string } | null
  /** Append-only trail of every routing action. Rendered as History. */
  events: AssignmentEvent[]
}

// Role vocabulary lives in `lib/roles`; re-exported so existing imports keep working.
// Only reviewer/sme/admin/viewer are assignable — a client is a counterparty on the
// engagement, never a review holder.
export type { Role } from "@/lib/roles"

export interface ReviewClientAttachment {
  clientId: string
  clientName: string
  memberCount: number
}

/**
 * Backend Comment, projected for the UI. `authorSide` is the server's
 * derivation of who authored it — never trusted from the client. There is
 * no `internal` flag: comments are equally visible to every side that has
 * access to the review. If we ever need reviewer-only notes, they will be
 * a separate resource, not a boolean on this one.
 */
export interface Message {
  id: MessageId
  authorName: string
  authorSide: AuthorSide
  body: string
  at: string
  /** Null = review-level; otherwise attached to a specific finding. */
  findingId: FindingId | null
  /** True when the current principal authored it. Client-side derived. */
  mine: boolean
}

export type LiveState = "live" | "reconnecting" | "offline"

export interface Review {
  id: ReviewId
  epaRegNo: string
  productName: string
  productKind: string
  version: string
  fplName: string
  masterName: string
  fplUrl: string
  masterUrl: string
  pageCount: number
  createdAt: string
  updatedAt: string
  assignment: ReviewAssignment
  client: ReviewClientAttachment | null
  findings: Finding[]
  messages: Message[]
}

/* ─── Pure helpers ─────────────────────────────────────────────────────
 * Deterministic and framework-free. Safe to call from render, tests, or
 * a Node script. Do not import React or DOM APIs into this section.
 */

/** Sorts findings within a KPI group by their display number, ascending. */
export function sortByNumber(findings: readonly Finding[]): Finding[] {
  return [...findings].sort((a, b) => a.number - b.number)
}

/**
 * Group findings by their KPI group, ordered by group.ordinal, and
 * numerically sorted within each group. Skips empty groups.
 */
export function groupFindings(findings: readonly Finding[]): Array<{
  group: FindingGroup
  items: Finding[]
}> {
  const map = new Map<string, { group: FindingGroup; items: Finding[] }>()
  for (const f of findings) {
    const entry = map.get(f.group.id)
    if (entry) entry.items.push(f)
    else map.set(f.group.id, { group: f.group, items: [f] })
  }
  return [...map.values()]
    .sort((a, b) => a.group.ordinal - b.group.ordinal)
    .map(({ group, items }) => ({ group, items: sortByNumber(items) }))
}

/** True when any finding is with a specialist and thus blocks the client report. */
export function hasSpecialistBlocker(findings: readonly Finding[]): boolean {
  return findings.some((f) => f.axes.verdict === "needs_sme")
}

/** Count of findings still awaiting a verdict. */
export function undecidedCount(findings: readonly Finding[]): number {
  return findings.filter((f) => f.axes.verdict === "unset").length
}

/** Findings that carry no on-label location — rendered in a rail card, not the overlay. */
export function documentPlacementFindings(findings: readonly Finding[]): Finding[] {
  return findings.filter((f) => f.placement === "document")
}

/** Filters shown as saved-view chips in the rail. Each is a predicate. */
export type SavedViewId =
  | "all"
  | "needs_decision"
  | "blocking_report"
  | "not_addressed"
  | "client_disputed"

export function matchesSavedView(view: SavedViewId, f: Finding): boolean {
  switch (view) {
    case "all":
      return true
    case "needs_decision":
      return f.axes.verdict === "unset"
    case "blocking_report":
      return f.axes.verdict === "needs_sme"
    case "not_addressed":
      // "Was there before, still open" — carried_over or regression AND state=open.
      return (
        f.axes.state === "open" &&
        (f.axes.origin === "carried_over" || f.axes.origin === "regression")
      )
    case "client_disputed":
      return f.axes.clientResolution === "disputed"
  }
}

/** Free-text match across rule id, group label, and claim text. */
export function matchesSearch(f: Finding, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (q.length === 0) return true
  return (
    f.ruleId.toLowerCase().includes(q) ||
    f.group.label.toLowerCase().includes(q) ||
    f.claim.toLowerCase().includes(q) ||
    String(f.number).includes(q)
  )
}

/** Human labels — the client-facing strings. Never mutate. */
export const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  error: "Error",
  warning: "Warning",
  info: "Info",
}

export const VERDICT_LABEL: Record<FindingAxes["verdict"], string> = {
  unset: "Undecided",
  confirmed: "Confirmed",
  rejected: "Dismissed",
  needs_sme: "With a specialist",
}

export const STATE_LABEL: Record<FindingAxes["state"], string> = {
  open: "Open",
  candidate_corrected: "Correction proposed",
  corrected_confirmed: "Corrected",
  resolved: "Resolved",
}

export const CLIENT_LABEL: Record<FindingAxes["clientResolution"], string> = {
  none: "No client response",
  acknowledged: "Acknowledged",
  disputed: "Disputed",
  done: "Client resolved",
}

export const ORIGIN_LABEL: Record<FindingAxes["origin"], string> = {
  new: "New this version",
  carried_over: "Carried over",
  regression: "Regression",
  manual: "Reviewer added",
}

export const STAGE_LABEL: Record<ReviewStage, string> = {
  initial: "Initial",
  secondary: "Secondary",
  done: "Done",
}

export const STAGE_ORDER: readonly ReviewStage[] = ["initial", "secondary", "done"]

/**
 * Human labels for the assignment trail. Verbs, past tense — the trail
 * is a record of what happened, not a menu of what can happen.
 */
export const ACTION_LABEL: Record<AssignmentAction, string> = {
  assign: "Assigned",
  reassign: "Reassigned",
  throwback: "Returned to pool",
  advance_stage: "Advanced",
  skip_secondary: "Skipped secondary",
  archive: "Archived",
  unarchive: "Unarchived",
}

export const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  reviewer: "Reviewer",
  sme: "Specialist",
  viewer: "Viewer",
  client: "Client",
}

export const AUTHOR_SIDE_LABEL: Record<AuthorSide, string> = {
  reviewer: "Reviewer",
  client: "Client",
  ai: "AI",
}
